<template>
    <div class="page-default page--orders">
        <SidebarVue />
        <div class="content">
            <StatusBar :title="statusTitle" :showQuota="false" :showMenu="false" :showTpe="false" />
            <div class="content_inner">
                <div class="tabs">
                    <a @click="isProduct = false" class="tab" :class="{active: !isProduct}">Billetterie</a>

                </div>

                <div class="searchForm">
                    <FormKit
                        type="text"
                        name="search"
                        v-model="searchString"
                        placeholder="Entrez un nom, numéro de commande..."
                    />
                    <FormKit
                        type="select"
                        name="sessionId"
                        placeholder="Choisissez une session de vente"
                        v-model="sessionId"
                        @change="updateSession"
                        :value="'all'"
                        :options="sessionsList"
                    />
                    <div class="count">
                        <template v-if="isProduct">
                            <p><span>{{ sellingSessionStore.stats.ordersBuvetteCount }}</span> commande(s)</p>
                            <p><span>{{ sellingSessionStore.stats.productsCount }}</span> produits(s)</p>
                        </template>
                        <template v-else>
                            <p><span>{{ sellingSessionStore.stats.ordersTicketingCount }}</span> commande(s)</p>
                            <p><span>{{ sellingSessionStore.stats.ticketsCount }}</span> billet(s)</p>
                        </template>
                    </div>
                </div>

                <div class="table_result">
                    <MTable
                        @clicked="onClickOrder"
                        v-if="tableData.length"
                        :showLabels="true"
                        :rows="tableData"
                        :selectable="true"
                        :labels="tableLabel"
                    />

                    <div class="no-result" v-else>
                        {{ $t('generic.noresult') }}
                    </div>

                    <Pagination
                        v-if="totalOrders > 0"
                        :current-page="currentPage"
                        :total-items="totalOrders"
                        :items-per-page="itemsPerPage"
                        @page-changed="onPageChanged"
                    />
                </div>

            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
    import StatusBar from '@/components/organisms/StatusBar.vue';
import SidebarVue from '@/components/organisms/Sidebar.vue';
import { useOrderStore } from '@/stores/OrderStore';
import { useSellingSessionStore } from '@/stores/SellingSessionStore';
import { ref, computed, watch, onUnmounted, onMounted } from 'vue';
import MTable from '@/components/molecules/Table.vue';
import Pagination from '@/components/atoms/Pagination.vue';
import { AppPrice } from '@/tools/Price';
import shoetest from 'shoetest';
import { useRoute, useRouter } from 'vue-router';
import { useEventStore } from '@/stores/EventStore';

    const sellingSessionStore = useSellingSessionStore()
    const orderStore = useOrderStore()
    const eventStore = useEventStore()
    const sessionId = ref(null)
    const searchString = ref(null)
    const itemsPerPage = 50

    const router = useRouter()
    const route = useRoute()

    const queryStringIsProduct = (route.query.isProduct && route.query.isProduct === '1') ? true : false
    const isProduct = ref<boolean>(queryStringIsProduct)

    onUnmounted(() => {
        orderStore.$reset()
    })
    onMounted(async () => {
        await sellingSessionStore.fetchAll()
        await fetchData()
    })

    const fetchData = (async (page: number = 1) => {
        const idSession = (sessionId.value) ? sessionId.value : null
        await orderStore.fetchAllOrders(sessionId.value, isProduct.value, page, itemsPerPage)
        await sellingSessionStore.fetchStats(idSession)
    })

    /**
     * @TODO : Refacto la partie date.
     */
    const formatDate = ((dateTime) => {
        let dateString = ''
        const date = new Date(dateTime)
        dateString += date.toLocaleDateString('fr', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        })
        dateString += ' à '
        dateString += date.toLocaleTimeString('fr', {
            hour: '2-digit',
            minute: '2-digit'
        })
        return dateString
    })

    const statusTitle = computed(() => {
        if (eventStore.currentEvent) {
            return eventStore.currentEvent.name + ' - Commandes'
        }
        return 'Commandes'
    })

    const onClickOrder = ((row) => {
        router.push({name: 'order_details', params: { orderId: row.$id }})
    })

    const sessionsList = computed(() => {
        const sellingSessions = sellingSessionStore.sellingSessions.map(sellingSession => {
            let label = `${sellingSession.authorId?.firstName} ${sellingSession.authorId?.lastName} - ${formatDate(sellingSession.createdAt)}`
            if (sellingSession.status == 'opened') {
                label += ' (en cours)'
            }
            return {
                label,
                value: sellingSession._id
            }
        })
        return [
            {
                label: "Toutes les sessions",
                value: null
            },
            ...sellingSessions
        ]
    })

    const tableLabel = computed(() => {
        const data = [
            {
                label: "Date",
                value: "date",
                align: "left"
            },
            {
                label: "Informations",
                value: "infos",
                align: "right"
            },
            {
                label: "Numéro",
                value: "number",
                align: "right"
            },
            {
                label: "Montant",
                value: "price",
                align: "right"
            }
        ]
        if (isProduct.value) {
            data.push({
                label: "Emplacement",
                value: "zone",
                align: "right"
            })
        }
        return data
    })

    const getSearchableOrderInfos = ((order) => {
        const infos = []
        if (order && order.invoiceData) {
            if (order.invoiceData.firstName) {
                infos.push(order.invoiceData.firstName)
            }
            if (order.invoiceData.lastName) {
                infos.push(order.invoiceData.lastName)
            }
            if (order.invoiceData.company) {
                infos.push(order.invoiceData.company)
            }
            if (order.orderNumber) {
                infos.push(order.orderNumber)
            }
        }
        return infos.join(' ')
    })

    const getLabelOrderInfos = ((order) => {
        const infos = []
        if (order && order.invoiceData) {
            if (order.invoiceData.firstName) {
                infos.push(order.invoiceData.firstName)
            }
            if (order.invoiceData.lastName) {
                infos.push(order.invoiceData.lastName)
            }
            if (infos.length == 0 && order.invoiceData.email) {
                infos.push(order.invoiceData.email)
            }
            if (order.invoiceData.company) {
                infos.push(`(${order.invoiceData.company})`)
            }
        }
        return (infos.length) ? infos.join(' ') : '- Anonyme -'
    })

    const filteredOrders = computed(() => {
        if (!orderStore.orders) return []

        return orderStore.orders.filter(order => {
            if (searchString.value) {
                const searchable = getSearchableOrderInfos(order)
                return shoetest.test(searchString.value.trim(), searchable)
            }
            return true
        })
    })

    const tableData = computed(() => {
        return filteredOrders.value.map(order => {
            // Affichage du total final (assurance et autres
            // ajustements inclus). Fallback `sellingPrice` si l'API
            // ne renvoie pas encore `totalWithAdjustments`.
            const totalFinal =
                order.totalPrice?.totalWithAdjustments
                ?? order.totalPrice?.sellingPrice
            const priceStr = order.totalPrice
                ? new AppPrice(totalFinal ?? 0).divide100().toString()
                : '--'

            // Si la commande a une assurance, on substitue la valeur
            // string par un objet enrichi (cf. MTable) pour afficher
            // un picto bouclier avec tooltip. Sinon on garde la
            // string telle quelle (rétro-compat).
            const insuranceAmount = order.insurance?.amount ?? 0
            const hasInsurance = order.hasInsurance && insuranceAmount > 0
            const insurancePrice = hasInsurance
                ? new AppPrice(insuranceAmount).divide100().toString()
                : ''

            const data: Record<string, any> = {
                $id: order._id,
                date: formatDate(order.createdAt),
                infos: getLabelOrderInfos(order),
                number: order.orderNumber,
                price: hasInsurance
                    ? {
                        value: priceStr,
                        icon: 'shield',
                        title: `Inclut une assurance (${insurancePrice})`,
                    }
                    : priceStr,
            }
            if (isProduct.value) {
                data['zone'] = (order.invoiceData) ? order.invoiceData.zone : '--'
            }
            return data
        })
    })

    const currentPage = computed(() => orderStore.currentPage)
    const totalOrders = computed(() => orderStore.totalOrders)

    const onPageChanged = async (page: number) => {
        await fetchData(page)
    }

    watch(isProduct, async () => {
        await fetchData(1)
    })

    watch(searchString, () => {
        // Note: La recherche se fait côté frontend sur les données paginées actuelles
        // Si vous voulez une recherche complète, il faudrait l'implémenter côté backend
    })

    const updateSession = (async () => {
        await fetchData(1)
    })


</script>